import { getOrdinalSuffixedNumber } from '@fruitful/fruitils';
import {
    type ImAccount,
    type MoneyMapRuleWithAccount,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import React from 'react';
import {
    BodyMediumRegular,
    LabelRegular,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitReviewContext,
    type IncomeSplitRuleSet,
    type PaycheckRoutingRuleAccountMetadata,
} from '../types/IncomeSplit';
import { getIncomeSplitAccountVisuals } from '../utils/incomeSplitAccountUtils';
import {
    getAccountFromRuleNew,
    isBackdoorTradRule,
    sortIncomeSplitRules,
} from '../utils/incomeSplitUtilsNew';
import IncomeSplitRuleCard from './IncomeSplitRuleCardNew';

export const IncomeSplitRuleSetReviewCard = ({
    group,
    bankAccountDetails,
    externalAccounts,
    internalInvestmentAccounts,
    moneyMapRulesWithProposedAccounts,
    context,
    moneyMap, // needed to get goal descriptions
    index,
    isMoneySystem = false,
}: {
    group: IncomeSplitRuleSet;
    bankAccountDetails:
        | PaycheckRoutingRuleAccountMetadata[]
        | BankAccountDisplayDetail[]
        | DepositAccount[];
    externalAccounts?: DepositAccount[];
    internalInvestmentAccounts?: ImAccount[];
    moneyMapRulesWithProposedAccounts?: MoneyMapRuleWithAccount[];
    context: IncomeSplitReviewContext;
    moneyMap?: MoneyMapWithRulesAndAccounts;
    index: number;
    isMoneySystem?: boolean;
}) => {
    const { rules } = group;
    const sortFn = sortIncomeSplitRules(moneyMap);
    const sortedRules = rules.sort(sortFn);
    const isGoalGroup = group.moneyMapRuleGroupId != null;

    return (
        <YStack
            backgroundColor="$gray200"
            borderRadius={24}
            padding={16}
            gap={12}
        >
            <XStack gap={8} alignItems="center">
                {isGoalGroup && isMoneySystem && (
                    <View
                        width={32}
                        height={32}
                        borderRadius={100}
                        backgroundColor="$petalDarkest"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <LabelRegular color="$petalLight">
                            {getOrdinalSuffixedNumber(index)}
                        </LabelRegular>
                    </View>
                )}
                <BodyMediumRegular>{group.name}</BodyMediumRegular>
            </XStack>
            <View gap={8}>
                {sortedRules.map((rule, _, array) => {
                    const mmRule = moneyMap?.moneyMapRules?.find(
                        (r) => r.id === rule.moneyMapRuleId,
                    );
                    const account = getAccountFromRuleNew({
                        rule,
                        bankAccountDetails,
                        externalAccounts: externalAccounts ?? [],
                        moneyMapRulesWithProposedAccounts:
                            moneyMapRulesWithProposedAccounts ?? [],
                        internalInvestmentAccounts:
                            internalInvestmentAccounts ?? [],
                    });
                    if (!account) return null;

                    const { color, icon } =
                        getIncomeSplitAccountVisuals(account);

                    const isBackdoorTrad = isBackdoorTradRule({
                        rule,
                        internalInvestmentAccounts,
                        moneyMapRulesWithProposedAccounts,
                    });
                    const ruleWithDescription = {
                        ...rule,
                        ...(array.length > 1 && isGoalGroup && isMoneySystem
                            ? { description: mmRule?.description }
                            : {}),
                    };
                    return (
                        <IncomeSplitRuleCard
                            key={
                                ruleWithDescription.id ??
                                ruleWithDescription.moneyMapRuleId
                            }
                            rule={ruleWithDescription}
                            account={account}
                            color={color}
                            icon={icon}
                            context={context}
                            isBackdoorTrad={isBackdoorTrad}
                        />
                    );
                })}
            </View>
        </YStack>
    );
};

export default IncomeSplitRuleSetReviewCard;
